import type { getCouponUpdateState } from "features/subscriptions/utils";

import type {
  RentedSubscription,
  WorkspaceAccount,
  WorkspaceLicense,
} from "common/types";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

export function getRentalAccountsForSub(
  accounts: WorkspaceAccount[],
  subscriptionId: string,
): WorkspaceAccount[] {
  return accounts.filter(
    ({ type, subscription_id }) =>
      type === "rental" && subscription_id === subscriptionId,
  );
}

export function getRequiredAccountIdsCountForDowngrade(
  sub: RentedSubscription,
  targetCount: number,
  accounts: WorkspaceAccount[],
): number {
  const assignedCount = getRentalAccountsForSub(accounts, sub.id).length;

  return Math.max(0, assignedCount - targetCount);
}

export function getAssignedRentalAccountCount(
  licenses: WorkspaceLicense[],
): number {
  return licenses.filter(
    ({ source, account_id }) => source === "rental" && !!account_id,
  ).length;
}

// Only include fields that changed — backend requires count or item_price_id.
// Estimates always include count so they can be requested without changes
// (needed to read the currently applied coupon on dialog open)
export function buildRentalUpdateRequestBody({
  count,
  planPricingId,
  accountIds,
  couponUpdateState,
}: {
  count: number;
  planPricingId: string;
  accountIds?: string[];
  couponUpdateState?: CouponUpdateState;
}): Record<string, unknown> | null {
  const body: Record<string, unknown> = { count, item_price_id: planPricingId };

  if (accountIds?.length) {
    body.account_ids = accountIds;
  }
  if (couponUpdateState) {
    Object.assign(body, {
      coupon: couponUpdateState.coupon,
      remove_coupon: couponUpdateState.removeCoupon,
    });
  }

  return body;
}
